<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, inject, onBeforeMount, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useTaskStore } from "@core/stores/task.store"
import { TASK_STATE } from "@core/constants"
import { useFetchWrapper } from "@core/composables"
import {
    mapLegacyStatusToTaskState,
    DEFAULT_DATE_FORMAT,
} from "@modules/energy-manager/constants"
import moment from "moment"
import type {
    Folder,
    DashboardIotSynthesis,
} from "@modules/energy-manager/types/dashboard"
import type { Services } from "@services"
import { useRoute, useRouter } from "vue-router"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            loading: "Chargement en cours...",
            error_synthesis:
                "Impossible de charger les données de l'analyse. Veuillez réessayer dans quelques instants.",
            error_folder:
                "Impossible de charger le dossier sélectionné. Veuillez réessayer ou choisir un autre périmètre.",
            taskLabel: "Génération de la synthèse IoT",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const taskStore = useTaskStore()
const services = inject<Services>("services")

// ================================
// ÉTAT
// ================================
const folder = ref<Folder | null>(null)
const synthesis = ref<DashboardIotSynthesis | null>(null)
const form = ref({
    startDate: moment().subtract(6, "months").format(DEFAULT_DATE_FORMAT),
    endDate: moment().format(DEFAULT_DATE_FORMAT),
})
const loadingStartAnalyse = ref(false)

// ================================
// LOGIQUE MÉTIER (FETCH)
// ================================
const {
    fetch: fetchFolder,
    isFetching: isFetchingFolder,
    hasError: hasErrorFolder,
} = useFetchWrapper(async (controller?: AbortController) => {
    const folderId = route?.query?.folderId as string | undefined
    if (!folderId || !services?.$getFolderById) {
        folder.value = null
        return
    }
    try {
        folder.value = await services.$getFolderById({
            folderId,
            controller,
        })
    } catch (error: unknown) {
        if (
            error &&
            typeof error === "object" &&
            "code" in error &&
            error.code === "not_found"
        ) {
            folder.value = null
        } else {
            throw error
        }
    }
})

const {
    fetch: fetchSynthesis,
    isFetching: isFetchingSynthesis,
    hasError: hasErrorSynthesis,
    error: errorSynthesis,
} = useFetchWrapper(async (controller?: AbortController) => {
    if (!services?.$getDashboardIotAnalyse) return
    const folderId = route?.query?.folderId as string | undefined
    try {
        synthesis.value = await services.$getDashboardIotAnalyse({
            folderId: folderId || null,
            controller,
        })
    } catch (error: unknown) {
        if (
            error &&
            typeof error === "object" &&
            "code" in error &&
            error.code === "not_found_workflow_analyse"
        ) {
            synthesis.value = null
        } else {
            throw error
        }
    }
})

const { fetch: fetchCreateSynthesis } = useFetchWrapper(
    async (controller?: AbortController) => {
        if (!services?.$createDashboardIotAnalyse) {
            throw new Error("Service de création de synthèse non disponible")
        }
        const folderId = route?.query?.folderId as string | undefined
        const { task_id, event_name } =
            await services.$createDashboardIotAnalyse({
                folderId: folderId || null,
                dateFrom: form.value.startDate,
                dateTo: form.value.endDate,
                controller,
            })
        taskStore.followEvent(task_id, event_name, {
            taskname: t("taskLabel"),
            notification: false,
            autoDelete: true,
            onSuccess: () => {
                setTimeout(() => fetchSynthesis(), 5000)
            },
        })
        setTimeout(() => fetchSynthesis(), 2000)
    }
)

// ================================
// DONNÉES CALCULÉES
// ================================
const getCanCreateNewAnalyse = computed(() => {
    return (
        ((!!synthesis.value &&
            ![TASK_STATE.RECEIVED, TASK_STATE.STARTED].includes(
                mapLegacyStatusToTaskState(synthesis.value.status || "")
            )) ||
            !synthesis.value) &&
        !isFetchingSynthesis.value
    )
})

const getIsValidDates = computed(() => {
    return moment(form.value.startDate).diff(moment(form.value.endDate)) <= 0
})

const getDownloadable = computed(() => {
    return (
        !!synthesis.value &&
        mapLegacyStatusToTaskState(synthesis.value.status || "") ===
            TASK_STATE.SUCCESS
    )
})

const getRequiredWaiting = computed(() => {
    return [TASK_STATE.RECEIVED, TASK_STATE.STARTED].includes(
        mapLegacyStatusToTaskState(synthesis.value?.status || "")
    )
})

const loading = computed(
    () => isFetchingSynthesis.value || isFetchingFolder.value
)

const showErrorSynthesis = computed(
    () =>
        hasErrorSynthesis.value &&
        errorSynthesis.value?.code !== "not_found_workflow_analyse"
)

// ================================
// WATCHERS
// ================================
watch(
    () => route?.query?.folderId,
    () => {
        loadData()
    }
)

// ================================
// INTERACTIONS
// ================================
async function loadData() {
    await Promise.all([fetchFolder(), fetchSynthesis()])
}

async function onClickStartSynthesis() {
    if (loadingStartAnalyse.value || !getIsValidDates.value) return
    if (!form.value.startDate || !form.value.endDate) return
    try {
        loadingStartAnalyse.value = true
        await fetchCreateSynthesis()
    } catch (error) {
        console.error("Erreur lors du lancement de la synthèse:", error)
    } finally {
        loadingStartAnalyse.value = false
    }
}

function onUpdateFolderId(folderId: string | null) {
    const query = { ...(route?.query || {}) }
    if (folderId) query.folderId = folderId
    else delete query.folderId
    router.push({ query })
}

function onUpdateRefresh() {
    fetchSynthesis()
}

function onUpdateStartDate(date: string | null) {
    form.value.startDate =
        date || moment().subtract(6, "months").format(DEFAULT_DATE_FORMAT)
}

function onUpdateEndDate(date: string | null) {
    form.value.endDate = date || moment().format(DEFAULT_DATE_FORMAT)
}

// ================================
// LIFECYCLE
// ================================
onBeforeMount(() => {
    loadData()
})
</script>

<template>
    <div
        class="flex flex-col justify-center items-center max-w-4xl w-full mx-auto gap-6"
    >
        <!-- ================================ -->
        <!-- SÉLECTEUR DE PÉRIMÈTRE -->
        <!-- ================================ -->
        <DashboardIotScopeSelector
            :folder="folder"
            :loading="loading"
            @update:folder-id="onUpdateFolderId"
        />

        <!-- ================================ -->
        <!-- DERNIÈRE ANALYSE + EXPORT -->
        <!-- ================================ -->
        <DashboardIotLastAnalysis
            :updated-at="synthesis?.updatedAt || null"
            :downloadable="getDownloadable"
            :url-excel="synthesis?.url_excel || null"
            :date-expiration-rapport="
                synthesis?.date_expiration_rapport || null
            "
            :analyse-id="synthesis?._id || null"
            @update:refresh="onUpdateRefresh"
        />

        <!-- ================================ -->
        <!-- FORMULAIRE + ANALYSE EN COURS -->
        <!-- ================================ -->
        <DashboardIotAnalysisForm
            :can-create-new-analyse="getCanCreateNewAnalyse"
            :date-from="synthesis?.dateFrom ?? form.startDate"
            :date-to="synthesis?.dateTo ?? form.endDate"
            :is-valid-dates="getIsValidDates"
            :loading-start-analyse="loadingStartAnalyse"
            :required-waiting="getRequiredWaiting"
            :status="synthesis?.status || ''"
            :nb-finished-tasks="synthesis?.nb_finished_tasks ?? 0"
            :analyses-count="synthesis?.analyses?.length ?? 0"
            @update:start-date="onUpdateStartDate"
            @update:end-date="onUpdateEndDate"
            @click:start-synthesis="onClickStartSynthesis"
            @click:reload="loadData"
        />

        <!-- ================================ -->
        <!-- MESSAGES D'ERREUR -->
        <!-- ================================ -->
        <BaseMessage v-if="showErrorSynthesis" severity="error">
            <p>{{ t("error_synthesis") }}</p>
        </BaseMessage>

        <BaseMessage v-if="hasErrorFolder" severity="error">
            <p>{{ t("error_folder") }}</p>
        </BaseMessage>

        <!-- ================================ -->
        <!-- MESSAGE DE CHARGEMENT -->
        <!-- ================================ -->
        <p
            v-if="loading"
            class="flex flex-wrap items-center gap-2 text-sm text-primary-700"
        >
            {{ t("loading") }}
        </p>
    </div>
</template>
